import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subscription, take } from 'rxjs';
import { BaseService } from '@app/core/services/base.service';
import {
  CreateWorkOrderSiteQueryRequest as ApiCreateRequest,
  WorkOrderSiteQueryAttachmentDto,
  WorkOrderSiteQueryDto,
} from '@app/generated/model';
import { WorkOrderSiteQueryControllerService } from '@app/generated/service';
import {
  CreateWorkOrderSiteQueryRequest,
  SiteQueryAttachment,
  SiteQueryCaseType,
  SiteQueryReason,
  SiteQueryStatus,
  WorkOrderSiteQuery,
} from './site-query.model';

@Injectable({ providedIn: 'root' })
export class WorkOrderSiteQueryService extends BaseService {
  private readonly countsSubject = new BehaviorSubject<number>(0);
  readonly counts$ = this.countsSubject.asObservable();
  private countWorkOrderId: string | null = null;
  private countRequestVersion = 0;
  private countRequest?: Subscription;

  constructor(
    http: HttpClient,
    private readonly siteQueryController: WorkOrderSiteQueryControllerService,
  ) {
    super(http);
  }

  list(workOrderId: string): Observable<WorkOrderSiteQuery[]> {
    return this.siteQueryController
      .getSiteQueries(workOrderId)
      .pipe(map((siteQueries) => (siteQueries ?? []).map((siteQuery) => this.mapSiteQuery(siteQuery))));
  }

  loadCounts(workOrderId: string): void {
    if (!workOrderId) {
      this.resetCounts();
      return;
    }
    if (this.countWorkOrderId === workOrderId) {
      return;
    }

    this.countRequest?.unsubscribe();
    const requestVersion = ++this.countRequestVersion;
    this.countWorkOrderId = workOrderId;
    this.countsSubject.next(0);

    this.countRequest = this.list(workOrderId)
      .pipe(take(1))
      .subscribe({
        next: (siteQueries) => {
          if (requestVersion === this.countRequestVersion && this.countWorkOrderId === workOrderId) {
            this.countsSubject.next(siteQueries.length);
          }
        },
        error: () => {
          if (requestVersion === this.countRequestVersion && this.countWorkOrderId === workOrderId) {
            this.countsSubject.next(0);
          }
        },
      });
  }

  resetCounts(): void {
    this.countRequestVersion += 1;
    this.countRequest?.unsubscribe();
    this.countRequest = undefined;
    this.countWorkOrderId = null;
    this.countsSubject.next(0);
  }

  create(workOrderId: string, request: CreateWorkOrderSiteQueryRequest): Observable<WorkOrderSiteQuery> {
    return this.siteQueryController
      .createSiteQuery(workOrderId, {
        ...request,
        reason: request.reason as unknown as ApiCreateRequest['reason'],
        caseType: request.caseType as unknown as ApiCreateRequest['caseType'],
      })
      .pipe(map((siteQuery) => this.mapSiteQuery(siteQuery)));
  }

  uploadAttachment(workOrderId: string, siteQueryId: string, file: File): Observable<SiteQueryAttachment> {
    return this.siteQueryController
      .uploadAttachment2(workOrderId, siteQueryId, file)
      .pipe(map((attachment) => this.mapAttachment(attachment)));
  }

  downloadAttachment(workOrderId: string, siteQueryId: string, attachmentId: string): Observable<Blob> {
    return this.http.get(
      `${this.collectionUrl(workOrderId)}/${encodeURIComponent(siteQueryId)}/attachments/${encodeURIComponent(
        attachmentId,
      )}`,
      { responseType: 'blob' },
    );
  }

  private collectionUrl(workOrderId: string): string {
    return `${this.baseUrl}/work-orders/${encodeURIComponent(workOrderId)}/site-queries`;
  }

  private mapSiteQuery(dto: WorkOrderSiteQueryDto): WorkOrderSiteQuery {
    return {
      id: dto.id ?? '',
      workOrderId: dto.workOrderId ?? '',
      workOrderNumber: dto.workOrderNumber ?? '',
      documentType: dto.documentType ?? 'SQ',
      sqNumber: dto.sqNumber ?? null,
      revision: dto.revision ?? '01',
      taskId: dto.taskId ?? '',
      title: dto.title ?? '',
      deliveryId: dto.deliveryId ?? 'NA',
      site: dto.site ?? null,
      discipline: dto.discipline ?? null,
      reason: dto.reason as SiteQueryReason,
      caseType: dto.caseType as SiteQueryCaseType,
      quantityChange: dto.quantityChange ?? false,
      scheduleImpact: dto.scheduleImpact ?? false,
      materialNeed: dto.materialNeed ?? false,
      description: dto.description ?? '',
      suggestedSolution: dto.suggestedSolution ?? '',
      references: dto.references ?? null,
      selectedReferences: (dto.selectedReferences ?? [])
        .filter(
          (reference): reference is typeof reference & { type: 'TAG' | 'DOCUMENT'; id: string; number: string } =>
            !!reference.id && !!reference.type && !!reference.number,
        )
        .map((reference) => ({ type: reference.type, id: reference.id, number: reference.number })),
      initiatedBy: dto.initiatedBy ?? '',
      company: dto.company ?? null,
      queryDate: dto.queryDate ?? null,
      siteLeader: dto.siteLeader ?? null,
      integrationManager: dto.integrationManager ?? null,
      status: (dto.status as SiteQueryStatus) ?? SiteQueryStatus.Draft,
      createdAt: dto.createdAt ?? '',
      attachments: (dto.attachments ?? []).map((attachment) => this.mapAttachment(attachment)),
    };
  }

  private mapAttachment(dto: WorkOrderSiteQueryAttachmentDto): SiteQueryAttachment {
    return {
      id: dto.id ?? '',
      fileName: dto.fileName ?? '',
      contentType: dto.contentType,
      size: dto.size ?? 0,
      createdAt: dto.createdAt ?? '',
    };
  }
}
